import React, { useCallback, useState } from 'react';
import { t } from '@lingui/core/macro';
import styled, { css } from 'styled-components';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import useTheme from '@rover/kibble/themes/useTheme';
import { BackgroundColors } from '@rover/kibble/tokens/build/rover/es6/theme';

type Props = {
  children: React.ReactNode;
  canDismiss?: boolean;
  onDismissClick?: () => void;
  background?: BackgroundColors;
  className?: string;
};

type ScrollBoundaryProps = {
  offsetTop?: string | number;
};

// declaring styled components so they can be referenced in css
const FloatingFooterContainer = styled(Box)``;
const StyledClose = styled(Close)``;

const StyledButton = styled(Button)`
  // this has to be nested to override default Button styles
  ${StyledClose} {
    cursor: pointer;
    fill: ${({ theme }) => theme.colors.interactive_text.primary_base};
  }
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.background.tertiary};
  }
`;

const FloatingFooter = ({
  children,
  canDismiss = false,
  onDismissClick,
  background = 'secondary',
  className,
}: Props): JSX.Element => {
  const theme = useTheme();
  const [isSticky, setIsSticky] = useState(true);

  const onDismiss = useCallback(() => {
    onDismissClick?.();
    setIsSticky(false);
  }, [onDismissClick]);

  return (
    <FloatingFooterContainer
      py="4x"
      position={isSticky ? 'sticky' : 'static'}
      bottom="0"
      width="100%"
      borderTop={`1px solid ${theme.colors.border.secondary}`}
      background={background}
      sx={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)' }}
      zIndex={ZIndex.NAVBAR_FIXED.toString()}
      className={className}
    >
      <Section>
        {canDismiss ? (
          <Box px="6x">
            {isSticky && (
              <Box position="absolute" top="3x" right={['1x', '1x', '3x']}>
                <StyledButton
                  onClick={onDismiss}
                  size="small"
                  variant="flat"
                  aria-label={t`Dismiss`}
                  circular
                  icon={StyledClose}
                  data-testid="dismiss-button"
                />
              </Box>
            )}
            {children}
          </Box>
        ) : (
          children
        )}
      </Section>
    </FloatingFooterContainer>
  );
};

FloatingFooter.ScrollBoundary = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'offsetTop',
})<ScrollBoundaryProps>`
  position: relative;

  /* This targets the last element inside the scroll boundary before the floating footer.
   * The negative margin needs to be applied, otherwise there will be an $offsetTop-sized 
   * space between the content and the footer when scrolled to the bottom. 
   * This will override any margin-bottom on that element (usually a PageLayout). 
   * If that margin is necessary, wrap it in a container, or add the margin to the last 
   * child of that element. */
  & > :nth-last-child(2) {
    margin-bottom: ${(props) => css`calc(${props.offsetTop || 0} * -1) !important`};
  }

  & > ${FloatingFooterContainer}:last-child {
    margin-top: ${(props) => props.offsetTop || 0};
  }
`;

export default FloatingFooter;
